import type { MatchState, PlayerId } from '@signalclash/shared';
import { SIGNAL_LINK_RANGE_SQ } from '@signalclash/shared';
import type Phaser from 'phaser';

// V1.5+ Slice 2 — Cut Link / channel visual helpers. Co-located with the rest
// of the signal-link FX so the Renderer stays under the 600-line cap (Rule
// 15) and so all signal-network presentation lives in one file.
const COLOR_CUT_LINK = 0xe2543c;
const CUT_LINK_PULSE_PERIOD_MS = 500;
const CUT_LINK_RING_RADIUS = 32;        // matches Renderer.NODE_RADIUS + 6.
const CHANNEL_RING_RADIUS = 26;
const CHANNEL_RING_PULSE_PX = 5;
const CHANNEL_RING_PERIOD_MS = 600;
const COLOR_CHANNEL_RING = 0xffe24a;

const COLOR_SHIELD_BUBBLE = 0xa0e8ff;
const SHIELD_BUBBLE_LINE_ALPHA = 0.55;
const SHIELD_BUBBLE_FILL_ALPHA = 0.1;

// Co-located with drawSignalLinks to keep small overlay-helpers together; both
// are presentation-only V1+ FX layers.
export function drawShieldBubbles(g: Phaser.GameObjects.Graphics, state: MatchState): void {
  for (const bubble of Object.values(state.shieldBubbles)) {
    if (bubble.remaining <= 0) continue;
    g.lineStyle(2, COLOR_SHIELD_BUBBLE, SHIELD_BUBBLE_LINE_ALPHA);
    g.strokeCircle(bubble.pos.x, bubble.pos.y, bubble.radius);
    g.fillStyle(COLOR_SHIELD_BUBBLE, SHIELD_BUBBLE_FILL_ALPHA);
    g.fillCircle(bubble.pos.x, bubble.pos.y, bubble.radius);
  }
}

// V1.5 Signal Link visualization per docs/02_GAME_DESIGN.md §11.
//
// Draws an animated pulse line between every adjacent owned-and-connected
// pair (Core ↔ first-hop relay, relay ↔ relay) using each owner's player
// color. Adjacency mirrors SignalLinkSystem's BFS rule: same owner + within
// SIGNAL_LINK_RANGE_PX.
//
// Isolated relays (owned but isConnected=false) get a dim crack overlay drawn
// here too — same layer keeps the dim/active distinction visually cohesive
// (Rule 21 one-second readability).

// V1.8 facelift — bumped player-0 blue from the old dim 0x3a7bd5 up to
// 0x4a96ff to match the rest of the game's palette (C_P0). The old value
// made signal links look noticeably dimmer than the cores/relays they
// connected.
const COLOR_PLAYER_ZERO = 0x4a96ff;
const COLOR_PLAYER_ONE = 0xd55a3a;
const COLOR_NEUTRAL = 0x5a6470;
const COLOR_ISOLATED = 0x8a8d92;
const NODE_RADIUS_FOR_OVERLAY = 18; // matches Renderer.NODE_RADIUS visually.

function colorForOwner(owner: PlayerId | null): number {
  if (owner === 0) return COLOR_PLAYER_ZERO;
  if (owner === 1) return COLOR_PLAYER_ONE;
  return COLOR_NEUTRAL;
}

// V1.5+ — signal "packets" traveling along each link line. A packet is a
// small bright dot whose distance along the line lerps from 0 → 1 over
// PACKET_PERIOD_MS, then wraps. Two packets per link, offset by half a
// period, so the line always shows visible movement and the eye can read
// the direction (always Core → relay → relay, attacker-side flows toward
// the contested centre). PACKET_SPEED_PX_S ≈ 200 — fast enough to feel
// alive at 1× zoom, slow enough to track at 0.5× zoom. Packets are not
// drawn on links shorter than 80 px (saves geometry on tightly-coupled
// home relays where the line is barely visible anyway).
const PACKET_PERIOD_MS = 2000;
const PACKET_RADIUS_PX = 4;
const PACKET_COUNT_PER_LINK = 2;
const PACKET_MIN_LINK_LEN_PX = 80;

function drawPackets(
  g: Phaser.GameObjects.Graphics,
  ax: number, ay: number, bx: number, by: number,
  color: number, nowMs: number, idHash: number,
): void {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < PACKET_MIN_LINK_LEN_PX) return;
  for (let k = 0; k < PACKET_COUNT_PER_LINK; k++) {
    // Each packet offset by k/COUNT of a period; idHash de-syncs neighbouring
    // links so the player sees a flowing network instead of marching columns.
    const tNorm = ((nowMs + idHash * 137) / PACKET_PERIOD_MS + k / PACKET_COUNT_PER_LINK) % 1;
    const px = ax + dx * tNorm;
    const py = ay + dy * tNorm;
    // Bright additive-feeling core + soft halo for readability against the
    // base line. Graphics layer doesn't support per-shape blend mode so we
    // get the halo via a larger semi-transparent disc behind the bright dot.
    g.fillStyle(color, 0.35);
    g.fillCircle(px, py, PACKET_RADIUS_PX + 3);
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(px, py, PACKET_RADIUS_PX);
    g.fillStyle(color, 1);
    g.fillCircle(px, py, PACKET_RADIUS_PX - 1.5);
  }
}

export function drawSignalLinks(
  g: Phaser.GameObjects.Graphics,
  state: MatchState,
  nowMs: number,
): void {
  g.clear();
  if (!state.enableSignalLinks) return;
  // Pulse phase: a slow sine so the connection lines breathe rather than
  // strobe — Rule 21 wants signal state legible in one second, but the
  // pulse should not draw the eye away from gameplay.
  const phase = (Math.sin((nowMs / 600) * Math.PI * 2) + 1) * 0.5;
  const pulseAlpha = 0.35 + 0.45 * phase;

  // V1.8 facelift — multi-pass neon-tube line: a wide soft outer glow
  // + a medium body + a bright thin core line. Sells "energy channel"
  // rather than "single drawn line." The pulse modulates all three.
  const drawNeonLine = (ax: number, ay: number, bx: number, by: number, color: number): void => {
    // Outer soft glow.
    g.lineStyle(9, color, 0.10 + 0.10 * phase);
    g.lineBetween(ax, ay, bx, by);
    // Mid body.
    g.lineStyle(4, color, pulseAlpha);
    g.lineBetween(ax, ay, bx, by);
    // Bright thin core line.
    g.lineStyle(1.5, 0xffffff, 0.45 + 0.30 * phase);
    g.lineBetween(ax, ay, bx, by);
  };

  for (const core of Object.values(state.cores)) {
    const fill = colorForOwner(core.ownerId);
    for (const node of Object.values(state.controlNodes)) {
      if (node.ownerId !== core.ownerId) continue;
      if (!node.isConnected) continue;
      const dx = node.pos.x - core.pos.x;
      const dy = node.pos.y - core.pos.y;
      if (dx * dx + dy * dy > SIGNAL_LINK_RANGE_SQ) continue;
      drawNeonLine(core.pos.x, core.pos.y, node.pos.x, node.pos.y, fill);
      // Packets flow OUTWARD from the Core toward the relay — visually
      // reads as "this is the player's own command network reaching out".
      drawPackets(g, core.pos.x, core.pos.y, node.pos.x, node.pos.y, fill, nowMs, core.id ^ node.id);
    }
  }

  const owned = Object.values(state.controlNodes).filter(
    (n) => n.ownerId !== null && n.isConnected,
  );
  for (let i = 0; i < owned.length; i++) {
    const a = owned[i]!;
    for (let j = i + 1; j < owned.length; j++) {
      const b = owned[j]!;
      if (a.ownerId !== b.ownerId) continue;
      const dx = b.pos.x - a.pos.x;
      const dy = b.pos.y - a.pos.y;
      if (dx * dx + dy * dy > SIGNAL_LINK_RANGE_SQ) continue;
      const color = colorForOwner(a.ownerId);
      drawNeonLine(a.pos.x, a.pos.y, b.pos.x, b.pos.y, color);
      drawPackets(g, a.pos.x, a.pos.y, b.pos.x, b.pos.y, color, nowMs, a.id ^ b.id);
    }
  }

  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === null) continue;
    if (node.isConnected) continue;
    g.lineStyle(1.5, COLOR_ISOLATED, 0.85);
    const r = NODE_RADIUS_FOR_OVERLAY + 4;
    g.lineBetween(node.pos.x - r, node.pos.y - r, node.pos.x + r, node.pos.y + r);
    g.lineBetween(node.pos.x - r, node.pos.y + r, node.pos.x + r, node.pos.y - r);
  }
}

// V1.5+ Slice 2 — pulse a red ring around any relay sitting in a Cut Link
// window. Drawn on top of the SignalLinkOverlay's isolation X (cut nodes are
// also forced isConnected=false), so the player sees BOTH the dim isolation
// and the bright "this is being cut" warning. Wall-clock-driven pulse phase
// (purely visual); the cut-active read is sim-driven.
export function drawCutLinkRings(
  g: Phaser.GameObjects.Graphics,
  state: MatchState,
  nowMs: number,
): void {
  // V1.8 facelift — 3-pass concentric ring (wide soft glow + body + thin
  // bright core) makes the cut-link warning READ as a hot threat indicator
  // instead of a flat circle outline.
  const phase = (nowMs % CUT_LINK_PULSE_PERIOD_MS) / CUT_LINK_PULSE_PERIOD_MS;
  const pulseAlpha = 0.55 + 0.35 * Math.sin(phase * Math.PI * 2);
  for (const node of Object.values(state.controlNodes)) {
    if (state.tick >= node.linkCutUntilTick) continue;
    g.lineStyle(7, COLOR_CUT_LINK, 0.18 * pulseAlpha);
    g.strokeCircle(node.pos.x, node.pos.y, CUT_LINK_RING_RADIUS);
    g.lineStyle(3, COLOR_CUT_LINK, pulseAlpha);
    g.strokeCircle(node.pos.x, node.pos.y, CUT_LINK_RING_RADIUS);
    g.lineStyle(1, 0xffd0c0, 0.85 * pulseAlpha);
    g.strokeCircle(node.pos.x, node.pos.y, CUT_LINK_RING_RADIUS);
  }
}

// V1.5+ Slice 2 — yellow pulse ring around a channeling squad. Same pulse
// shape as the Mark Target ring; only the colour distinguishes them so the
// visual language stays consistent (Rule 21 one-second readability).
export function drawChannelRing(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  nowMs: number,
): void {
  // V1.8 facelift — added a wide soft outer glow so the channel ring reads
  // as energy-cast (matches the cut-link / mark-target language).
  const phase = (nowMs % CHANNEL_RING_PERIOD_MS) / CHANNEL_RING_PERIOD_MS;
  const pulse = Math.sin(phase * Math.PI * 2) * CHANNEL_RING_PULSE_PX;
  g.lineStyle(6, COLOR_CHANNEL_RING, 0.15);
  g.strokeCircle(x, y, CHANNEL_RING_RADIUS + pulse);
  g.lineStyle(2, COLOR_CHANNEL_RING, 0.9);
  g.strokeCircle(x, y, CHANNEL_RING_RADIUS + pulse);
  g.lineStyle(1, COLOR_CHANNEL_RING, 0.45);
  g.strokeCircle(x, y, CHANNEL_RING_RADIUS + pulse + 4);
}

// V1 — pulsing red ring around a marked target. Pulse phase is wall-clock-
// driven (purely visual); the marked-state read is sim-driven. Co-located here
// with the other ring overlays so Renderer stays under the file-size cap.
const MARK_RING_RADIUS = 22;
const MARK_RING_PULSE_PX = 4;
const COLOR_MARK_RING = 0xff5d5d;
export function drawMarkRing(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  nowMs: number,
): void {
  // V1.8 facelift — added wide soft outer glow + a small inner reticle dot
  // so a marked unit reads as a tracked threat from any zoom level.
  const phase = (nowMs % 800) / 800;
  const pulse = Math.sin(phase * Math.PI * 2) * MARK_RING_PULSE_PX;
  // Outer soft glow
  g.lineStyle(6, COLOR_MARK_RING, 0.15);
  g.strokeCircle(x, y, MARK_RING_RADIUS + pulse);
  // Bright body
  g.lineStyle(2, COLOR_MARK_RING, 0.85);
  g.strokeCircle(x, y, MARK_RING_RADIUS + pulse);
  // Faint outer secondary ring
  g.lineStyle(1, COLOR_MARK_RING, 0.4);
  g.strokeCircle(x, y, MARK_RING_RADIUS + pulse + 4);
  // Tiny inner reticle dot — reads as "targeted" at a glance
  g.fillStyle(COLOR_MARK_RING, 0.65);
  g.fillCircle(x, y, 1.4);
}
